import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock, Trophy } from '../../../../assets/images';
import { formatDateTimeDisplay } from '../../../../utils/base';

const BoxData = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: '8px',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        opacity: 0.2,
        borderRadius: '8px',
        border: '1px solid white',
        zIndex: 1,
    },
}));

const RewardBox = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(90deg, #6c1d00 0%, #964400 100%)',
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px 30px',
    borderRadius: '10px',
    position: 'relative',
}));

const ChampionshipCard = ({
    name,
    type = 0,
    organizer,
    startDate = '',
    endDate = '',
    reward = '',
    rank = null,
    contentName,
    participants = '1명 1회',
    currentRank = '현재 1위',
    currentWinner,
    currentScore = '1 Wave',
    isLocked,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const displayName = name ?? '';
    const displayOrganizer = organizer ?? '';
    const displayContentName = contentName ?? t('content_name');
    const displayWinner = currentWinner ?? t('');

    return (
        <Box
            sx={{
                display: 'flex',
                margin: 0,
                padding: '20px',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                alignSelf: 'stretch',
            }}
        >
            {/* Period */}
            <Typography
                sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'white',
                    letterSpacing: '-0.36px',
                    textAlign: 'center',
                }}
            >
                {formatDateTimeDisplay(startDate, 'date') + ' ~ ' + formatDateTimeDisplay(endDate, 'date')}
            </Typography>

            {/* Main Content */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                {/* Title Section */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '5px',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        height: '44px',
                        width: '100%',
                    }}
                >
                    <Box sx={{ width: '20px', height: '20px', flexShrink: 0 }}>
                        {isLocked ? <Lock /> : <Unlock />}
                    </Box>

                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: 'white',
                            letterSpacing: '-0.54px',
                            textAlign: 'center',
                            lineHeight: '1.2',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            height: '43.2px',
                            maxHeight: '43.2px',
                        }}
                    >
                        {displayName}
                    </Typography>
                </Box>

                {/* Organizer */}
                <BoxData>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'white',
                            letterSpacing: '-0.36px',
                            position: 'relative',
                            zIndex: 2,
                        }}
                    >
                        {t('host')} : {displayOrganizer}
                    </Typography>
                </BoxData>

                {/* Reward */}
                <RewardBox>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 700,
                            color: theme.palette.figma.yellowText,
                            letterSpacing: '-0.42px',
                        }}
                    >
                        {rank ?? t('1st')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'white',
                            letterSpacing: '-0.42px',
                        }}
                    >
                        {reward}
                    </Typography>
                </RewardBox>

                {/* Challenge Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '15px 0',
                        borderRadius: '10px',
                        width: '100%',
                        position: 'relative',
                        border: '1px solid white',
                    }}
                >
                    {/* Challenge Title */}
                    <Typography
                        sx={{
                            fontSize: '24px',
                            fontWeight: 600,
                            color: 'white',
                            letterSpacing: '-0.72px',
                            textAlign: 'center',
                        }}
                    >
                        {displayContentName}
                    </Typography>

                    {/* Participants */}
                    <BoxData>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: 'white',
                                letterSpacing: '-0.36px',
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            {participants}
                        </Typography>
                    </BoxData>

                    {/* Current Status */}
                    {type === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '10px',
                                width: '100%',
                            }}
                        >
                            <Trophy color={theme.palette.figma.iconWhite} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: 'white',
                                    letterSpacing: '-0.42px',
                                }}
                            >
                                {currentRank}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: 'white',
                                    letterSpacing: '-0.42px',
                                }}
                            >
                                {displayWinner}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: 'white',
                                    letterSpacing: '-0.42px',
                                }}
                            >
                                {currentScore}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ChampionshipCard;
